import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { useField } from 'formik';
import styles from './style.module.scss';
import tiny from '../../../../assets/getstartedflow/weightChart/tiny.png';
import small from '../../../../assets/getstartedflow/weightChart/small.png';
import medium from '../../../../assets/getstartedflow/weightChart/medium.png';
import large from '../../../../assets/getstartedflow/weightChart/large.png';
import vlarge from '../../../../assets/getstartedflow/weightChart/vlarge.png';

/**
 * A component to input and guide users in selecting their dog's weight.
 */
const DogWeight = ({ values, errors, touched }: any) => {
  const [field, meta, helpers] = useField('weight');
  const { setValue } = helpers;

  // State for modal visibility
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // Weight chart data
  const chartData = [
    { image: tiny, label: 'Tiny', weight: '1-5 kg' },
    { image: small, label: 'Small', weight: '6-18 kg' },
    { image: medium, label: 'Medium', weight: '19-31 kg' },
    { image: large, label: 'Large', weight: '32-45 kg' },
    { image: vlarge, label: 'Very Large', weight: '+45 kg' },
  ];

  const MINIMUM = 1;
  const MAXIMUM = 80;

  /**
   * Handles validation on blur, ensuring the weight input is within valid range.
   */
  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value) {
      const numericValue = Number(value);
      if (numericValue > MAXIMUM) {
        setValue(MAXIMUM);
      } else if (numericValue < MINIMUM) {
        setValue(MINIMUM);
      }
    }
  };

  /**
   * Restricts the input to numeric values with up to two decimal places.
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    // Limit input to numbers and a single decimal point
    const cleanedString = input.replace(/[^0-9.]|(\.)(?=.*\.)+/g, '');

    // Limit to 2 decimal places
    const decimalLimited = cleanedString.includes('.')
      ? cleanedString.split('.').slice(0, 2).join('.').replace(/(\.\d{2})\d+/, '$1')
      : cleanedString;

    setValue(decimalLimited);
  };

  return (
    <div className={styles.weightContainer}>
      <Form.Group controlId="formDogsWeight" className={styles.weightContainer}>
        <div className={styles.inputContainer}>
          {/* Column 1 */}
          <div style={{ flex: "0 0 60%" }}>
            <Form.Label
              className={`${styles.weightLabel} ${(values?.dogsName || '').length > 10 ? styles.small : ''}`}
            >
              How much does your dog weigh?
            </Form.Label>
            <div className={styles.infoContainer} onClick={handleShow}>
              <span className={styles.info}>i</span>
              <span className={`${styles.infoMessage} cur-pointer font-sofia`}>
                I don’t know exact weight
              </span>
            </div>
          </div>

          {/* Column 2 */}
          <div style={{ flex: "0 0 40%", display: "flex", alignItems: "center", gap: "8px" }}>
            <Form.Control
              className={styles.weightInput}
              type="text"
              name="weight"
              placeholder=""
              value={values.weight}
              isInvalid={!!errors.dogsName && touched.dogsName}
              onChange={handleInputChange}
              onBlur={onBlur}
            />
            <span>kg</span>
          </div>
        </div>

        <Form.Control.Feedback type="invalid">{errors?.weight}</Form.Control.Feedback>
      </Form.Group>


      <Modal
        show={show}
        onHide={handleClose}
        centered
        dialogClassName="pet-weight-info-modal"
      >
        <Modal.Body className="steps-weight-modal">
          <div className="steps-weight-header">
            <div className="heading font-brandon">
              <span className="weight-title">How to weigh your dog at home</span>
            </div>
            <span className="close-button" onClick={handleClose}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M2 2L18 18"
                  stroke="#C7BFB7"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M18 2L2 18"
                  stroke="#C7BFB7"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </div>
          <div className="modalBody">
            <div>
              <p className="step-title-description">
                First, weigh yourself on the scales at home. Then pick up your dog and weigh both of you together. Subtract your original weight to determine your dog's weight. Easy as!
              </p>
            </div>
            <div className="weight-chart">
              {chartData.map((item, index) => (
                <div className="weight-chart-item" key={`weight-chart-item-${index}`}>
                  <img src={item.image} alt={`${item.label} weight chart`} />
                  <span className="label">{item.label}</span>
                  <span className="value">{item.weight}</span>
                </div>
              ))}
            </div>
            <div className="btn-wrapper text-center">
              <Button className="button-submit mgn-top-30" onClick={handleClose}>
                Got it!
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DogWeight;
